<template>
  <div ref="rootEl" class="date-picker pa-0">
    <v-menu
      v-model="menuOpen"
      content-class="portal-date-picker-menu"
      :close-on-content-click="false"
      :open-on-click="true"
      :open-on-focus="false"
      transition="scale-transition"
      location="bottom"
      :location-strategy="adaptiveLocationStrategy"
      :disabled="disabled || readonly"
    >
      <template #activator="{ props: menuProps }">
        <v-text-field
          v-bind="menuProps"
          :model-value="textDdMm"
          :label="label"
          variant="outlined"
          density="compact"
          :hide-details="hideDetails"
          :readonly="readonly"
          :disabled="disabled"
          :clearable="clearable && !readonly"
          autocomplete="off"
          maxlength="10"
          :append-inner-icon="readonly ? undefined : calendarIcon"
          :rules="fieldRules"
          @input="onTextDdMmInput"
          @blur="onBlurDisplay"
          @click:clear="onClearClick"
          @keydown="onKeydown"
        />
      </template>
      <v-date-picker
        v-model:view-mode="pickerViewMode"
        :model-value="pickerIsoForPicker"
        :min="minDate || undefined"
        :max="maxDate || undefined"
        color="primary"
        :first-day-of-week="1"
        :width="pickerWidth"
        show-adjacent-months
        hide-header
        hide-title
        @update:model-value="onPickerUpdate"
      >
        <template #controls="p">
          <div
            v-if="p.viewMode !== 'year'"
            class="portal-date-picker__toolbar d-flex align-center justify-space-between flex-grow-1 ga-1 px-1"
          >
            <template v-if="p.viewMode === 'month'">
              <v-btn
                icon
                variant="text"
                density="comfortable"
                :disabled="controlDisabled(p, 'prev-month')"
                aria-label="Mes anterior"
                @click="p.prevMonth"
              >
                <v-icon icon="mdi-chevron-left" size="24" />
              </v-btn>
              <v-btn
                variant="text"
                class="portal-date-picker__title-btn flex-grow-1 text-body-1 font-weight-medium text-none"
                rounded="lg"
                aria-label="Elegir año y mes"
                @click="p.openMonths"
              >
                {{ p.monthYearText }}
              </v-btn>
              <v-btn
                icon
                variant="text"
                density="comfortable"
                :disabled="controlDisabled(p, 'next-month')"
                aria-label="Mes siguiente"
                @click="p.nextMonth"
              >
                <v-icon icon="mdi-chevron-right" size="24" />
              </v-btn>
            </template>
            <template v-else-if="p.viewMode === 'months'">
              <v-btn
                icon
                variant="text"
                density="comfortable"
                :disabled="controlDisabled(p, 'prev-year')"
                aria-label="Año anterior"
                @click="p.prevYear"
              >
                <v-icon icon="mdi-chevron-left" size="24" />
              </v-btn>
              <v-btn
                variant="text"
                class="portal-date-picker__title-btn flex-grow-1 text-h6 font-weight-medium text-none"
                rounded="lg"
                aria-label="Elegir año"
                @click="p.openYears"
              >
                {{ p.yearText }}
              </v-btn>
              <v-btn
                icon
                variant="text"
                density="comfortable"
                :disabled="controlDisabled(p, 'next-year')"
                aria-label="Año siguiente"
                @click="p.nextYear"
              >
                <v-icon icon="mdi-chevron-right" size="24" />
              </v-btn>
            </template>
          </div>
        </template>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import icons from '../../utils/enums/icons'
import validationRules from '../../utils/helpers/rules'
import {
  formatIsoToDdMmYyyy,
  maskInputDdMmYyyy,
  normalizeIsoDateString,
  parseDdMmYyyyToIso,
} from '../../utils/helpers/portalDateFormat'

export default {
  name: 'DatePicker',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Fecha',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: [Boolean, String],
      default: 'auto',
    },
    minDate: {
      type: String,
      default: '',
    },
    maxDate: {
      type: String,
      default: '',
    },
    treat1900AsEmpty: {
      type: Boolean,
      default: true,
    },
    customRules: {
      type: Array,
      default: () => [],
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    forceMenuBelow: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      /** Vista del calendario: al elegir año Vuetify pasa a `month`; forzamos `months` para elegir mes antes del día. */
      pickerViewMode: 'month',
      /** Evita tratar el reset al abrir menú (year→month) como selección de año. */
      skipYearToMonthsAdjust: false,
      menuOpen: false,
      textDdMm: '',
      /** Cantidad de dígitos en `textDdMm` tras el último render (para la máscara). */
      ddMmDigitCount: 0,
      calendarIcon: icons.CALENDAR,
      updateMenuLocation: null,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 768,
    }
  },
  computed: {
    pickerIso() {
      const fromProp = this.normalizedModel
      if (fromProp) return fromProp
      return parseDdMmYyyyToIso(this.textDdMm) || undefined
    },
    /** Valor estable para VDatePicker (evita `undefined` en el picker). */
    pickerIsoForPicker() {
      return this.pickerIso ?? null
    },
    pickerWidth() {
      const available = Math.max(240, this.viewportWidth - 24)
      if (this.viewportWidth <= 420 || this.viewportHeight <= 620) {
        return Math.min(296, available)
      }
      return Math.min(320, available)
    },
    normalizedModel() {
      let iso = normalizeIsoDateString(this.modelValue)
      if (this.treat1900AsEmpty && iso === '1900-01-01') iso = ''
      return iso
    },
    fieldRules() {
      if (this.readonly) return []
      const extra = Array.isArray(this.customRules) ? [...this.customRules] : []
      const r = [...extra]
      if (this.required) r.push(validationRules.required)
      r.push(validationRules.validDdMmYyyy)
      r.push(validationRules.dateDdMmNotAfterToday)
      const minIso = normalizeIsoDateString(this.minDate)
      if (minIso) {
        r.push(validationRules.dateDdMmNotBeforeMinIso(minIso))
      }
      return r
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler() {
        this.syncTextFromModel()
      },
    },
    menuOpen(open) {
      if (!open) {
        this.removeOutsideCloseListeners()
        this.removeMenuPositionListeners()
        return
      }
      this.updateViewportMetrics()
      this.addOutsideCloseListeners()
      this.addMenuPositionListeners()
      this.skipYearToMonthsAdjust = true
      this.pickerViewMode = 'month'
      this.$nextTick(() => {
        this.skipYearToMonthsAdjust = false
        this.scheduleMenuLocationUpdate()
      })
    },
    pickerViewMode(newMode, oldMode) {
      if (this.skipYearToMonthsAdjust) return
      if (newMode === 'month' && oldMode === 'year') {
        this.pickerViewMode = 'months'
      }
      this.scheduleMenuLocationUpdate()
    },
  },
  beforeUnmount() {
    this.removeOutsideCloseListeners()
    this.removeMenuPositionListeners()
  },
  methods: {
    updateViewportMetrics() {
      if (typeof window === 'undefined') return
      const viewport = window.visualViewport
      this.viewportWidth = Math.round(viewport?.width || window.innerWidth || 1024)
      this.viewportHeight = Math.round(viewport?.height || window.innerHeight || 768)
    },
    addOutsideCloseListeners() {
      this.removeOutsideCloseListeners()
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true)
      document.addEventListener('keydown', this.onDocumentKeydown, true)
    },
    removeOutsideCloseListeners() {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true)
      document.removeEventListener('keydown', this.onDocumentKeydown, true)
    },
    addMenuPositionListeners() {
      this.removeMenuPositionListeners()
      window.addEventListener('resize', this.scheduleMenuLocationUpdate, { passive: true })
      window.addEventListener('scroll', this.scheduleMenuLocationUpdate, {
        passive: true,
        capture: true,
      })
      window.visualViewport?.addEventListener?.(
        'resize',
        this.scheduleMenuLocationUpdate,
        { passive: true },
      )
      window.visualViewport?.addEventListener?.(
        'scroll',
        this.scheduleMenuLocationUpdate,
        { passive: true },
      )
    },
    removeMenuPositionListeners() {
      window.removeEventListener('resize', this.scheduleMenuLocationUpdate)
      window.removeEventListener('scroll', this.scheduleMenuLocationUpdate, true)
      window.visualViewport?.removeEventListener?.('resize', this.scheduleMenuLocationUpdate)
      window.visualViewport?.removeEventListener?.('scroll', this.scheduleMenuLocationUpdate)
      this.updateMenuLocation = null
    },
    scheduleMenuLocationUpdate() {
      this.updateViewportMetrics()
      if (!this.menuOpen || typeof this.updateMenuLocation !== 'function') return
      requestAnimationFrame(() => {
        if (this.menuOpen && typeof this.updateMenuLocation === 'function') {
          this.updateMenuLocation()
        }
      })
    },
    eventIsInsidePicker(event) {
      const target = event?.target
      if (!(target instanceof Node)) return false
      const root = this.$refs.rootEl
      if (root?.contains?.(target)) return true
      return !!target.closest?.('.portal-date-picker-menu')
    },
    onDocumentPointerDown(event) {
      if (!this.menuOpen || this.eventIsInsidePicker(event)) return
      this.menuOpen = false
    },
    onDocumentKeydown(event) {
      if (!this.menuOpen) return
      if (event.key !== 'Enter' && event.key !== 'Escape') return
      this.menuOpen = false
    },
    adaptiveLocationStrategy(data, props, contentStyles) {
      const updateLocation = () => {
        const target = data.target.value
        const content = data.contentEl.value
        if (!target || !content) return

        const targetRect = target.getBoundingClientRect()
        const viewport = window.visualViewport || {
          width: window.innerWidth,
          height: window.innerHeight,
          offsetLeft: 0,
          offsetTop: 0,
        }
        const margin = 12
        const gap = 4
        const contentRect = content.getBoundingClientRect()
        const viewportLeft = viewport.offsetLeft
        const viewportTop = viewport.offsetTop
        const viewportRight = viewportLeft + viewport.width
        const viewportBottom = viewportTop + viewport.height
        const maxContentWidth = Math.max(160, viewport.width - margin * 2)
        const measuredWidth =
          contentRect.width ||
          content.offsetWidth ||
          content.scrollWidth ||
          320
        const measuredHeight =
          contentRect.height ||
          content.offsetHeight ||
          content.scrollHeight ||
          320
        const naturalWidth = Math.max(measuredWidth, targetRect.width)
        const naturalHeight = measuredHeight
        const contentWidth = Math.min(naturalWidth, maxContentWidth)
        const spaces = {
          bottom: viewportBottom - targetRect.bottom - gap - margin,
          top: targetRect.top - viewportTop - gap - margin,
        }
        const clamp = (value, min, max) => Math.max(min, Math.min(value, max))
        const horizontalLeft = () => {
          return clamp(targetRect.left, viewportLeft + margin, viewportRight - contentWidth - margin)
        }
        const candidates = [
          {
            key: 'bottom',
            fits: spaces.bottom >= naturalHeight,
            space: spaces.bottom,
            top: targetRect.bottom + gap,
            left: horizontalLeft(),
            origin: 'top left',
            anchor: 'bottom left',
            maxHeight: spaces.bottom,
          },
          {
            key: 'top',
            fits: spaces.top >= naturalHeight,
            space: spaces.top,
            top: targetRect.top - gap - naturalHeight,
            left: horizontalLeft(),
            origin: 'bottom left',
            anchor: 'top left',
            maxHeight: spaces.top,
          },
        ]
        const selected = candidates.find((candidate) => candidate.fits)
          || candidates.reduce((best, candidate) => (
            candidate.space > best.space ? candidate : best
          ), candidates[0])
        const maxHeight = Math.max(160, viewport.height - margin * 2)
        const positionedHeight = Math.min(naturalHeight, maxHeight)
        let top = selected.top
        let left = selected.left
        if (!selected.fits && selected.key === 'top') {
          top = targetRect.top - gap - positionedHeight
        }
        top = clamp(top, viewportTop + margin, viewportBottom - positionedHeight - margin)
        left = clamp(left, viewportLeft + margin, viewportRight - contentWidth - margin)

        Object.assign(contentStyles.value, {
          position: 'fixed',
          top: `${Math.round(top)}px`,
          left: `${Math.round(left)}px`,
          right: undefined,
          bottom: undefined,
          minWidth: `${Math.round(targetRect.width)}px`,
          maxWidth: `${Math.round(maxContentWidth)}px`,
          maxHeight: undefined,
          overflowY: undefined,
          transformOrigin: selected.origin,
          '--v-overlay-anchor-origin': selected.anchor,
        })
      }

      this.updateMenuLocation = updateLocation
      requestAnimationFrame(updateLocation)
      return { updateLocation }
    },
    onClearClick() {
      if (this.readonly || this.disabled) return
      this.textDdMm = ''
      this.ddMmDigitCount = 0
      this.menuOpen = false
      this.$emit('update:modelValue', '')
    },
    onTextDdMmInput(event) {
      if (this.readonly || this.disabled) return
      const input = event.target
      const val = input.value
      const start = input.selectionStart
      const end = input.selectionEnd
      const direction = input.selectionDirection || 'none'
      const prev = this.ddMmDigitCount
      const masked = maskInputDdMmYyyy(val, prev)
      this.textDdMm = masked
      this.ddMmDigitCount = (masked.match(/\d/g) || []).length
      // Vue may replace the input value when the mask inserts/removes slashes.
      // Restore the selection by digit position after that DOM update.
      const position = (offset) => {
        const prefix = val.slice(0, offset)
        const digits = (prefix.match(/\d/g) || []).length
        let index = 0
        let count = 0
        while (index < masked.length && count < digits) {
          if (/\d/.test(masked[index])) count++
          index++
        }
        if (masked[index] === '/' && (prefix.endsWith('/') || offset === val.length)) index++
        return index
      }
      this.$nextTick(() => {
        input.value = masked
        if (start != null && end != null) {
          input.setSelectionRange(position(start), position(end), direction)
        }
      })
    },
    syncTextFromModel() {
      const iso = this.normalizedModel
      this.textDdMm = iso ? formatIsoToDdMmYyyy(iso) : ''
      this.ddMmDigitCount = (this.textDdMm.match(/\d/g) || []).length
    },
    /** Valor emitido por VDatePicker (string ISO, Date, timestamp, etc.) → YYYY-MM-DD local. */
    isoFromPickerValue(val) {
      if (val == null || val === '') return ''
      const raw = Array.isArray(val) ? val[0] : val
      if (raw == null || raw === '') return ''
      if (typeof raw === 'string') {
        return normalizeIsoDateString(raw)
      }
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        return this.isoFromPickerValue(new Date(raw))
      }
      if (raw instanceof Date || (typeof raw === 'object' && typeof raw.getFullYear === 'function')) {
        const d = raw instanceof Date ? raw : new Date(raw.getFullYear(), raw.getMonth(), raw.getDate())
        if (Number.isNaN(d.getTime())) return ''
        const y = d.getFullYear()
        const mo = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}-${mo}-${day}`
      }
      return ''
    },
    onPickerUpdate(val) {
      let iso = this.isoFromPickerValue(val)
      if (!iso) return
      const minIso = normalizeIsoDateString(this.minDate)
      if (iso && minIso && iso < minIso) {
        iso = minIso
      }
      if (this.treat1900AsEmpty && iso === '1900-01-01') {
        this.$emit('update:modelValue', '')
        this.textDdMm = ''
        this.ddMmDigitCount = 0
      } else {
        this.$emit('update:modelValue', iso)
        this.textDdMm = iso ? formatIsoToDdMmYyyy(iso) : ''
        this.ddMmDigitCount = (this.textDdMm.match(/\d/g) || []).length
      }
      this.menuOpen = false
    },
    onBlurDisplay() {
      const s = String(this.textDdMm ?? '').trim()
      if (s === '') {
        this.$emit('update:modelValue', '')
        this.ddMmDigitCount = 0
        return
      }

      let iso = parseDdMmYyyyToIso(s)
      if (!iso) {
        this.syncTextFromModel()
        return
      }

      const minIso = normalizeIsoDateString(this.minDate)
      if (minIso && iso < minIso) {
        iso = minIso
      }

      if (this.treat1900AsEmpty && iso === '1900-01-01') {
        this.$emit('update:modelValue', '')
        this.textDdMm = ''
        this.ddMmDigitCount = 0
        return
      }
      this.$emit('update:modelValue', iso)
      this.textDdMm = formatIsoToDdMmYyyy(iso)
      this.ddMmDigitCount = (this.textDdMm.match(/\d/g) || []).length
    },
    onKeydown(e) {
      if (e.key === 'Tab') this.menuOpen = false
    },
    controlDisabled(p, key) {
      const d = p?.disabled
      return Array.isArray(d) && d.includes(key)
    },
  },
}
</script>

<style scoped>
.date-picker :deep(.v-date-picker-controls),
.date-picker :deep(.v-date-picker-header) {
  padding-top: 4px;
}
.portal-date-picker__toolbar {
  min-height: 48px;
  max-width: 100%;
}
.portal-date-picker__title-btn {
  letter-spacing: normal;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

/* Días: chip redondeado al estilo Vuetify 3 (selección primary) */
.date-picker :deep(.v-date-picker-month__day--selected .v-btn.v-date-picker-month__day-btn) {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;
  border-radius: 9999px !important;
  font-weight: 600;
}
.date-picker :deep(.v-date-picker-month__day-btn) {
  border-radius: 9999px !important;
}
/* Meses / años en grillas: refuerzo chip pill */
.date-picker :deep(.v-date-picker-months__content .v-btn--variant-flat) {
  border-radius: 9999px !important;
}
.date-picker :deep(.v-date-picker-years__content .v-btn--variant-flat) {
  border-radius: 9999px !important;
}
</style>

<style>
.portal-date-picker-menu {
  overflow: visible !important;
}

.portal-date-picker-menu .v-date-picker {
  width: min(320px, calc(100vw - 24px)) !important;
}

.portal-date-picker-menu .v-date-picker .v-picker__body {
  width: 100% !important;
}

.portal-date-picker-menu .v-date-picker-controls {
  min-height: 42px !important;
  padding: 0 8px !important;
}

.portal-date-picker-menu .portal-date-picker__toolbar {
  min-height: 42px !important;
}

.portal-date-picker-menu .portal-date-picker__title-btn {
  min-height: 36px !important;
  font-size: 1rem !important;
}

.portal-date-picker-menu .v-date-picker-month {
  min-width: 0 !important;
  width: 100% !important;
  padding: 0 10px 10px !important;
}

.portal-date-picker-menu .v-date-picker-month__days,
.portal-date-picker-menu .v-date-picker-month__weekdays {
  display: grid !important;
  grid-template-columns: repeat(7, minmax(0, 1fr)) !important;
  width: 100% !important;
}

.portal-date-picker-menu .v-date-picker-month__weekday {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 0 !important;
  height: 32px !important;
  font-size: 0.9rem !important;
}

.portal-date-picker-menu .v-date-picker-month__day {
  display: flex !important;
  justify-content: center !important;
  min-width: 0 !important;
  height: 34px !important;
}

.portal-date-picker-menu .v-date-picker-month__day .v-btn {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  font-size: 0.92rem !important;
}

.portal-date-picker-menu .v-date-picker-months__content,
.portal-date-picker-menu .v-date-picker-years__content {
  width: 100% !important;
  min-width: 0 !important;
}

/* Overlay del menú: fuera del scope de .date-picker → clase content-class del v-menu */
.portal-date-picker-menu .v-date-picker-years__content {
  display: flex !important;
  flex-direction: column !important;
  grid-template-columns: unset !important;
  align-items: stretch !important;
  justify-content: flex-start !important;
  gap: 4px !important;
  padding-inline: 12px !important;
  padding-block: 8px !important;
}

.portal-date-picker-menu .v-date-picker-years__content .v-btn {
  width: 100% !important;
  justify-content: center !important;
}

/* Sin barra de controles en vista año (solo lista de años) */
.portal-date-picker-menu .v-date-picker--year .v-date-picker-controls {
  display: none !important;
  min-height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}

@media (max-width: 420px), (max-height: 620px) {
  .portal-date-picker-menu .v-date-picker {
    width: min(296px, calc(100vw - 24px)) !important;
  }

  .portal-date-picker-menu .v-date-picker-controls {
    min-height: 38px !important;
    padding: 0 6px !important;
  }

  .portal-date-picker-menu .portal-date-picker__toolbar {
    min-height: 38px !important;
  }

  .portal-date-picker-menu .portal-date-picker__title-btn {
    font-size: 1rem !important;
    min-height: 34px !important;
  }

  .portal-date-picker-menu .v-date-picker-month {
    min-width: 0 !important;
    padding: 0 8px 8px !important;
  }

  .portal-date-picker-menu .v-date-picker-month__weekday {
    height: 30px !important;
    font-size: 0.875rem !important;
  }

  .portal-date-picker-menu .v-date-picker-month__days {
    row-gap: 1px !important;
  }

  .portal-date-picker-menu .v-date-picker-month__day {
    height: 31px !important;
  }

  .portal-date-picker-menu .v-date-picker-month__day .v-btn {
    width: 28px !important;
    height: 28px !important;
    font-size: 0.875rem !important;
  }

  .portal-date-picker-menu .v-date-picker-months__content,
  .portal-date-picker-menu .v-date-picker-years__content {
    padding-block: 6px !important;
  }

  .portal-date-picker-menu .v-date-picker-months__content .v-btn,
  .portal-date-picker-menu .v-date-picker-years__content .v-btn {
    min-height: 30px !important;
    height: 30px !important;
    font-size: 0.875rem !important;
  }
}
</style>
